import {
  FunnelDatalayerEvent,
  SharedFunnelDatalayerEvent,
} from '../_services/types/datalayer.types';
import { Step } from '../_services/types/step.type';
import { BusinessClaimType, ClaimType } from '../enums/claim/claim-type.enum';
import { BUSINESS_INSURANCE_BASE } from '../insurance-flows/business-insurances/company-insurance-constants';
import {
  BUSINESS_ACCIDENT_BASE_URL,
  BUSINESS_ACCIDENT_FUNNEL_EVENT_BASE,
} from './business-accident-components/business-accident-constants';
import {
  BUSINESS_COMPANY_RC_BASE_URL,
  BUSINESS_COMPANY_RC_FUNNEL_EVENT_BASE,
} from './business-company-rc-components/business-company-rc-constants';
import {
  BUSINESS_COMPANY_THINGS_BASE_URL,
  BUSINESS_COMPANY_THINGS_FUNNEL_EVENT_BASE,
} from './business-company-things-components/business-company-things-constants';
import {
  BUSINESS_HEALTH_BASE_URL,
  BUSINESS_HEALTH_FUNNEL_EVENT_BASE,
} from './business-health-components/business-health-constants';
import {
  getGenericBusinessClaimFunnelEventBase,
  getGenericClaimBaseUrl,
} from './generic-business-claim-components/generic-business-claim-constants';

export const BUSINESS_CLAIM_BASE_URL = `${BUSINESS_INSURANCE_BASE}/sinistre`;
export const CLAIM_FORM_BASE_URL = 'formulaire';

export const BUSINESS_CLAIM_FORM_FULL_URL = `${BUSINESS_CLAIM_BASE_URL}/${CLAIM_FORM_BASE_URL}`;

export const CLAIM_GENERAL_FUNNEL_EVENT_BASE: SharedFunnelDatalayerEvent = {
  product_category: 'General',
  product_name: 'Claim',
  funnel_complete: false,
  get event() {
    return `${this.product_name}_${this.product_category}`;
  },
};

export const BUSINESS_CLAIM_STEPS: {
  ACCOUNT: Step;
  LOGIN: Step;
  REGISTER: Step;
  DOCUMENT_UPLOAD: Step;
  END: Step;
} = {
  ACCOUNT: {
    step: [1, 1],
    url: 'compte',
    datalayerEvent: {
      ...CLAIM_GENERAL_FUNNEL_EVENT_BASE,
      funnel_stage: 1,
      stage_name: 'Compte',
    },
  },
  LOGIN: {
    step: [1, 1],
    url: 'connexion',
    datalayerEvent: {
      ...CLAIM_GENERAL_FUNNEL_EVENT_BASE,
      funnel_stage: 2,
      stage_name: 'Connexion',
    },
  },
  REGISTER: {
    step: [1, 1],
    url: 'inscription',
    datalayerEvent: {
      ...CLAIM_GENERAL_FUNNEL_EVENT_BASE,
      funnel_stage: 2,
      stage_name: 'Inscription',
    },
  },
  DOCUMENT_UPLOAD: {
    step: [2, 1],
    url: 'documents',
    datalayerEvent: {
      ...CLAIM_GENERAL_FUNNEL_EVENT_BASE,
      funnel_stage: 3,
      stage_name: 'Documents',
    },
  },
  END: {
    url: 'fin',
    step: [0, 0],
    datalayerEvent: {
      ...CLAIM_GENERAL_FUNNEL_EVENT_BASE,
      funnel_stage: -1,
      stage_name: 'Fin',
      funnel_complete: true,
    },
  },
};

export const BUSINESS_CLAIM_ROUTES_MAPPING: { [key in BusinessClaimType]: string } = {
  [ClaimType.BUSINESS_ACCIDENT]: BUSINESS_ACCIDENT_BASE_URL,
  [ClaimType.BUSINESS_HEALTH]: BUSINESS_HEALTH_BASE_URL,
  [ClaimType.BUSINESS_RC_COMPANY]: BUSINESS_COMPANY_RC_BASE_URL,
  [ClaimType.BUSINESS_COMPANY_THINGS]: BUSINESS_COMPANY_THINGS_BASE_URL,
  [ClaimType.BUSINESS_MOTOR_VEHICLE]: getGenericClaimBaseUrl(ClaimType.BUSINESS_MOTOR_VEHICLE),
  [ClaimType.BUSINESS_RETIREMENT]: getGenericClaimBaseUrl(ClaimType.BUSINESS_RETIREMENT),
  [ClaimType.BUSINESS_LEGAL_PROTECTION]: getGenericClaimBaseUrl(
    ClaimType.BUSINESS_LEGAL_PROTECTION,
  ),
  [ClaimType.BUSINESS_BUILDING]: getGenericClaimBaseUrl(ClaimType.BUSINESS_BUILDING),
  [ClaimType.BUSINESS_CONSTRUCTION]: getGenericClaimBaseUrl(ClaimType.BUSINESS_CONSTRUCTION),
  [ClaimType.BUSINESS_TRANSPORT]: getGenericClaimBaseUrl(ClaimType.BUSINESS_TRANSPORT),
  [ClaimType.BUSINESS_CYBERSECURITY]: getGenericClaimBaseUrl(ClaimType.BUSINESS_CYBERSECURITY),
  [ClaimType.BUSINESS_TECHNICAL_INSTALLATION]: getGenericClaimBaseUrl(
    ClaimType.BUSINESS_TECHNICAL_INSTALLATION,
  ),
};

export const END_DATALAYER_EVENT_MAPPING: { [key in BusinessClaimType]: FunnelDatalayerEvent } = {
  [ClaimType.BUSINESS_ACCIDENT]: {
    ...BUSINESS_ACCIDENT_FUNNEL_EVENT_BASE,
    funnel_complete: true,
    stage_name: 'fin',
    funnel_stage: 11,
  },
  [ClaimType.BUSINESS_HEALTH]: {
    ...BUSINESS_HEALTH_FUNNEL_EVENT_BASE,
    funnel_complete: true,
    stage_name: 'fin',
    funnel_stage: 9,
  },
  [ClaimType.BUSINESS_RC_COMPANY]: {
    ...BUSINESS_COMPANY_RC_FUNNEL_EVENT_BASE,
    funnel_complete: true,
    stage_name: 'fin',
    funnel_stage: 8,
  },
  [ClaimType.BUSINESS_COMPANY_THINGS]: {
    ...BUSINESS_COMPANY_THINGS_FUNNEL_EVENT_BASE,
    funnel_complete: true,
    stage_name: 'fin',
    funnel_stage: 9,
  },
  [ClaimType.BUSINESS_MOTOR_VEHICLE]: {
    ...getGenericBusinessClaimFunnelEventBase(ClaimType.BUSINESS_MOTOR_VEHICLE),
    funnel_complete: true,
    stage_name: 'fin',
    funnel_stage: 7,
  },
  [ClaimType.BUSINESS_RETIREMENT]: {
    ...getGenericBusinessClaimFunnelEventBase(ClaimType.BUSINESS_RETIREMENT),
    funnel_complete: true,
    stage_name: 'fin',
    funnel_stage: 7,
  },
  [ClaimType.BUSINESS_LEGAL_PROTECTION]: {
    ...getGenericBusinessClaimFunnelEventBase(ClaimType.BUSINESS_LEGAL_PROTECTION),
    funnel_complete: true,
    stage_name: 'fin',
    funnel_stage: 7,
  },
  [ClaimType.BUSINESS_BUILDING]: {
    ...getGenericBusinessClaimFunnelEventBase(ClaimType.BUSINESS_BUILDING),
    funnel_complete: true,
    stage_name: 'fin',
    funnel_stage: 7,
  },
  [ClaimType.BUSINESS_CONSTRUCTION]: {
    ...getGenericBusinessClaimFunnelEventBase(ClaimType.BUSINESS_CONSTRUCTION),
    funnel_complete: true,
    stage_name: 'fin',
    funnel_stage: 7,
  },
  [ClaimType.BUSINESS_TRANSPORT]: {
    ...getGenericBusinessClaimFunnelEventBase(ClaimType.BUSINESS_TRANSPORT),
    funnel_complete: true,
    stage_name: 'fin',
    funnel_stage: 7,
  },
  [ClaimType.BUSINESS_CYBERSECURITY]: {
    ...getGenericBusinessClaimFunnelEventBase(ClaimType.BUSINESS_CYBERSECURITY),
    funnel_complete: true,
    stage_name: 'fin',
    funnel_stage: 7,
  },
  [ClaimType.BUSINESS_TECHNICAL_INSTALLATION]: {
    ...getGenericBusinessClaimFunnelEventBase(ClaimType.BUSINESS_TECHNICAL_INSTALLATION),
    funnel_complete: true,
    stage_name: 'fin',
    funnel_stage: 7,
  },
};
